/**
 * ActionContent — operational action forms rendered inside a FloatingWindow.
 * Ported from the old ActionModalRegistry, minus the blocking scrim shell.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowUpRight } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { useDemoMode } from "@/lib/demoMode";
import FrictionAudit from "@/components/FrictionAudit";

const inputCls = "bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none px-3 py-2 text-nex-silver font-mono text-sm transition-all duration-200";
const submitCls = "w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-nex-gold text-nex-navy font-mono text-[0.7rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright disabled:opacity-60 transition-all duration-200";

const Field = ({ label, children, testId }) => (
  <label className="flex flex-col gap-1" data-testid={testId}>
    <span className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-muted uppercase">{label}</span>
    {children}
  </label>
);

/* ── 1. Brief Intake ─────────────────────────────────────────────── */
export function BriefIntakeContent({ win }) {
  const { closeWindow } = useFloatingWindows();
  const demoMode = useDemoMode();
  return (
    <div data-testid="nds-action-brief-intake" className="bg-[#0A0F1C]">
      <FrictionAudit embedded demoMode={demoMode} onDone={() => closeWindow(win.id)} />
    </div>
  );
}

/* ── 2. New Contact ──────────────────────────────────────────────── */
export function NewContactContent({ win }) {
  const { closeWindow } = useFloatingWindows();
  const demoMode = useDemoMode();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ full_name: "", email: "", phone: "", business_name: "", industry: "", stage: "new" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (ev) => {
    ev?.preventDefault();
    if (!f.full_name || !f.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    // Demo mode: never touch the real CRM. Fake the round-trip so the visitor
    // sees the exact same UX (spinner → toast → close) but no record is created.
    if (demoMode) {
      await new Promise((r) => setTimeout(r, 600));
      toast.success(`Demo · Contact "${f.full_name}" would sync to your CRM`);
      setSaving(false);
      closeWindow(win.id);
      return;
    }
    try {
      await apiClient.post("/crm/contacts", { ...f, source: "os" });
      toast.success(`Contact created · ${f.full_name}`);
      closeWindow(win.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-3" data-testid="nds-action-new-contact">
      <Field label="Full Name *" testId="nds-contact-name">
        <input className={inputCls} value={f.full_name} onChange={set("full_name")} autoFocus />
      </Field>
      <Field label="Email *" testId="nds-contact-email">
        <input type="email" className={inputCls} value={f.email} onChange={set("email")} />
      </Field>
      <Field label="Phone"><input className={inputCls} value={f.phone} onChange={set("phone")} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Business Name"><input className={inputCls} value={f.business_name} onChange={set("business_name")} /></Field>
        <Field label="Industry"><input className={inputCls} value={f.industry} onChange={set("industry")} /></Field>
      </div>
      <Field label="Stage">
        <select className={inputCls} value={f.stage} onChange={set("stage")}>
          <option value="new">New</option>
          <option value="qualified">Qualified</option>
          <option value="proposal">Proposal</option>
          <option value="won">Won</option>
        </select>
      </Field>
      <button type="submit" disabled={saving} data-testid="nds-contact-submit" className={submitCls}>
        <Send className="w-4 h-4" /> {saving ? "Saving…" : "Create Contact"}
      </button>
    </form>
  );
}

/* ── 3. Send SMS ─────────────────────────────────────────────────── */
export function SendSmsContent({ win }) {
  const { closeWindow } = useFloatingWindows();
  const demoMode = useDemoMode();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ phone: "", message: "", recipient_name: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (ev) => {
    ev?.preventDefault();
    if (!f.phone || !f.message) { toast.error("Phone and message required"); return; }
    setSaving(true);
    if (demoMode) {
      await new Promise((r) => setTimeout(r, 600));
      toast.success("Demo · SMS would send via your SMSGate account");
      setSaving(false);
      closeWindow(win.id);
      return;
    }
    try {
      await apiClient.post("/admin/sms/test", { ...f, recipient_kind: "contact" });
      toast.success("SMS sent");
      closeWindow(win.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-3" data-testid="nds-action-send-sms">
      <Field label="Phone *" testId="nds-sms-phone">
        <input className={inputCls} value={f.phone} onChange={set("phone")} placeholder="+1 214-555-0134" autoFocus />
      </Field>
      <Field label="Recipient Name">
        <input className={inputCls} value={f.recipient_name} onChange={set("recipient_name")} />
      </Field>
      <Field label="Message *" testId="nds-sms-message">
        <textarea className={inputCls} rows={4} value={f.message} onChange={set("message")} />
      </Field>
      <button type="submit" disabled={saving} data-testid="nds-sms-submit" className={submitCls}>
        <Send className="w-4 h-4" /> {saving ? "Sending…" : "Send"}
      </button>
    </form>
  );
}

/* ── 4. Deep-link fallback ───────────────────────────────────────── */
export function OpenModuleContent({ win }) {
  const { closeWindow } = useFloatingWindows();
  const demoMode = useDemoMode();
  const { description, href, title } = win.payload || {};
  // In demo mode, block navigation to real /admin routes and instead nudge
  // the prospect toward the Friction Audit CTA.
  const showLink = href && !demoMode;
  return (
    <div className="p-5 space-y-4" data-testid="nds-action-open-module">
      {description && <p className="text-nex-silver/85 leading-relaxed text-sm">{description}</p>}
      {demoMode && (
        <div className="rounded-md border border-nex-gold/25 bg-nex-gold/5 px-3 py-2 font-mono text-[0.65rem] tracking-[0.14em] uppercase text-nex-gold/90">
          Demo · {title || "This module"} ships live in your engine
        </div>
      )}
      {showLink ? (
        <Link
          to={href}
          onClick={() => closeWindow(win.id)}
          data-testid="nds-open-module-go"
          className={submitCls + " no-underline"}
        >
          Open Live Module <ArrowUpRight className="w-4 h-4" />
        </Link>
      ) : demoMode ? (
        <Link
          to="/#friction-audit"
          onClick={() => closeWindow(win.id)}
          data-testid="nds-open-module-demo-cta"
          className={submitCls + " no-underline"}
        >
          Book Your Engine <ArrowUpRight className="w-4 h-4" />
        </Link>
      ) : (
        <div className="w-full px-4 py-3 border border-white/10 bg-white/[0.02] text-center font-mono text-[0.7rem] tracking-[0.22em] uppercase text-nex-muted">
          Coming Soon
        </div>
      )}
    </div>
  );
}
